import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export const SEOContentSection = () => {
  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* What is Text Case Conversion */}
          <div className="mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
              What is Text Case Conversion?
            </h2>
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="leading-relaxed mb-4">
                Text case conversion is the process of transforming text from one capitalization 
                style to another. Whether you&apos;re formatting a document, preparing content for 
                social media, writing code, or organizing data, proper text casing ensures your 
                content looks professional and is easy to read.
              </p>
              <p className="leading-relaxed mb-4">
                An online text case converter like CaseFlip allows you to instantly change the 
                case of any text without manual editing. Instead of retyping entire paragraphs 
                or using complex find-and-replace operations, you can transform thousands of 
                words in a single click.
              </p>
              <p className="leading-relaxed">
                This is especially useful for writers who receive text in ALL CAPS, marketers 
                preparing headlines, developers working with variable names, students formatting 
                citations, and anyone who needs consistent text formatting quickly.
              </p>
            </div>
          </div>

          {/* Case Types Explained */}
          <div className="mb-16">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-6">
              Understanding Different Case Formats
            </h2>
            <div className="grid gap-6">
              <div className="p-5 rounded-xl bg-muted/30 border border-border/50">
                <h3 className="font-semibold text-foreground mb-2">Sentence Case</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Capitalizes only the first letter of each sentence, like standard written English. 
                  <span className="block mt-2 font-medium text-foreground/80">Example: &quot;The quick brown fox jumps over the lazy dog.&quot;</span>
                </p>
              </div>
              <div className="p-5 rounded-xl bg-muted/30 border border-border/50">
                <h3 className="font-semibold text-foreground mb-2">Lower Case</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Converts all letters to lowercase. Useful for data normalization and certain coding conventions.
                  <span className="block mt-2 font-medium text-foreground/80">Example: &quot;the quick brown fox jumps over the lazy dog.&quot;</span>
                </p>
              </div>
              <div className="p-5 rounded-xl bg-muted/30 border border-border/50">
                <h3 className="font-semibold text-foreground mb-2">UPPER CASE</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Converts all letters to uppercase. Often used for acronyms, headlines, or emphasis.
                  <span className="block mt-2 font-medium text-foreground/80">Example: &quot;THE QUICK BROWN FOX JUMPS OVER THE LAZY DOG.&quot;</span>
                </p>
              </div>
              <div className="p-5 rounded-xl bg-muted/30 border border-border/50">
                <h3 className="font-semibold text-foreground mb-2">Capitalized Case</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Capitalizes the first letter of every word. Good for names and certain heading styles.
                  <span className="block mt-2 font-medium text-foreground/80">Example: &quot;The Quick Brown Fox Jumps Over The Lazy Dog.&quot;</span>
                </p>
              </div>
              <div className="p-5 rounded-xl bg-muted/30 border border-border/50">
                <h3 className="font-semibold text-foreground mb-2">Title Case</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Capitalizes major words while keeping minor words (articles, prepositions) lowercase. 
                  Follows standard title formatting rules for books, articles, and headlines.
                  <span className="block mt-2 font-medium text-foreground/80">Example: &quot;The Quick Brown Fox Jumps over the Lazy Dog&quot;</span>
                </p>
              </div>
              <div className="p-5 rounded-xl bg-muted/30 border border-border/50">
                <h3 className="font-semibold text-foreground mb-2">aLtErNaTiNg CaSe</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Alternates between lowercase and uppercase for each character. Popular for stylistic 
                  or humorous effect in social media and memes.
                  <span className="block mt-2 font-medium text-foreground/80">Example: &quot;tHe QuIcK bRoWn FoX jUmPs OvEr ThE lAzY dOg.&quot;</span>
                </p>
              </div>
              <div className="p-5 rounded-xl bg-muted/30 border border-border/50">
                <h3 className="font-semibold text-foreground mb-2">iNVERSE cASE</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Swaps the case of each letter — uppercase becomes lowercase and vice versa.
                  <span className="block mt-2 font-medium text-foreground/80">Example: &quot;tHE QUICK BROWN FOX&quot; → &quot;The quick brown fox&quot;</span>
                </p>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-6">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="border-border/50">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  Is my text data saved or tracked?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  No. CaseFlip processes all text entirely in your browser using JavaScript. 
                  Your text never leaves your device, is never sent to any server, and is not 
                  stored or logged in any way. We don&apos;t use cookies for tracking, and there&apos;s 
                  no analytics on the text you convert.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="border-border/50">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  Is there a limit to how much text I can convert?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  There&apos;s no hard limit on text length. You can convert paragraphs, entire 
                  documents, or even book-length content. However, extremely large texts 
                  (millions of characters) may slow down your browser.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="border-border/50">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  Does it preserve my original formatting?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes! CaseFlip preserves all your original spacing, line breaks, and paragraph 
                  structure. Only the letter casing is changed — everything else stays exactly 
                  as you entered it.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="border-border/50">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  Can I use this on my phone?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Absolutely. CaseFlip is fully responsive and works great on smartphones and 
                  tablets. Just visit the site in your mobile browser, paste your text, and 
                  convert away.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-5" className="border-border/50">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  Do I need to create an account?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  No account is required. CaseFlip is designed for immediate use. There&apos;s no 
                  sign-up, no login, and no email required. Just open the page and start 
                  converting.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-6" className="border-border/50">
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  Is CaseFlip really free?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes, the core text conversion features are completely free and will remain 
                  free. We may introduce optional premium features in the future, but the 
                  basic converter will always be available at no cost.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SEOContentSection;
